/**
 * /locations/:citySlug — first-class clinic detail page.
 *
 * Each rendering is unique per clinic: local hero, address, hours, parking,
 * directions, service-area context, and local FAQ come from `data/clinics.js`.
 * The rest of the page — consultation-modality explanation, eligibility and
 * regulatory disclaimers, links to treatments/providers/how-it-works — is the
 * shared authoritative content the site already publishes. No community
 * doorway pages. No invented reviews. No appointment or availability
 * promises. No superlatives.
 */
import { Link, useParams, Navigate } from "react-router-dom";
import {
  MapPin, Phone, Mail, Clock, Video, ShieldCheck, ArrowRight,
  Stethoscope, ClipboardList, TestTube2, Building2, MessageCircle,
} from "lucide-react";
import { trackPhoneCall } from "../lib/analytics";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
// ClinicalReviewByline belongs on editorial article pages only, not clinic location pages.
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";
import { findClinicBySlug, CLINICS } from "../data/clinics";

const ORIGIN = "https://medicalpeptides.ai";

// Locally-available peptide-treatment categories. These link to the existing
// canonical treatment/peptide pages — the clinical monographs live there, not
// here. This satisfies "linked to the existing canonical treatment pages,
// never duplicating the full clinical monographs".
const TREATMENT_CATEGORIES = [
  { title: "Weight management", href: "/peptide/semaglutide",           blurb: "Provider evaluation for GLP-1 and metabolic-support options where clinically appropriate." },
  { title: "Hormone optimization", href: "/peptide-library",            blurb: "Provider-led review of hormone-related concerns and age-appropriate therapy considerations." },
  { title: "Recovery & performance", href: "/peptide/bpc-157",          blurb: "Evaluation-based discussion of peptides that may support recovery goals under medical guidance." },
  { title: "Healthy aging", href: "/peptide/nad-plus",                  blurb: "Provider-led discussion of longevity-focused options based on your history and objectives." },
  { title: "Hair & skin", href: "/peptide/ghk-cu",                      blurb: "Assessment for aesthetic-focused peptide options where medically appropriate." },
  { title: "General wellness", href: "/find-my-treatment",              blurb: "Not sure where to start? Our providers help identify whether peptide therapy fits your goals." },
];

const RELATED_LINKS = [
  { href: "/how-it-works",         label: "How treatment works",   icon: ClipboardList },
  { href: "/providers",            label: "Meet the providers",    icon: Stethoscope },
  { href: "/peptide-library",      label: "Treatment catalog",     icon: Building2 },
  { href: "/pricing",              label: "Pricing philosophy",    icon: MessageCircle },
  { href: "/medical-disclaimer",   label: "Medical disclaimer",    icon: ShieldCheck },
  { href: "/locations",            label: "All locations",         icon: MapPin },
];

function buildSchema(c) {
  const clinic = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: c.display_name,
    url: `${ORIGIN}/locations/${c.slug}`,
    telephone: c.phone_e164,
    email: c.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: c.street,
      addressLocality: c.city,
      addressRegion: c.state,
      postalCode: c.zip,
      addressCountry: "US",
    },
    // Only the state we are licensed in — no invented regional claims.
    areaServed: { "@type": "State", name: c.state_full },
    openingHoursSpecification: c.hours_schema,
    parentOrganization: {
      "@type": "MedicalOrganization",
      name: "VidaVital Medical",
      url: ORIGIN,
    },
    // Deliberate omissions: geo (no coordinates), sameAs (no unverified
    // profiles), aggregateRating (banned per PRD lock), image (avoid
    // stock-photo confusion).
  };
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.local_faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Locations", item: `${ORIGIN}/locations` },
      { "@type": "ListItem", position: 3, name: `${c.name}, ${c.state}`, item: `${ORIGIN}/locations/${c.slug}` },
    ],
  };
  const webpage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${c.display_name} \u2014 MedicalPeptides.ai`,
    url: `${ORIGIN}/locations/${c.slug}`,
    isPartOf: { "@type": "WebSite", url: ORIGIN, name: "MedicalPeptides.ai" },
  };
  return [clinic, faq, breadcrumb, webpage];
}

export default function ClinicLocation() {
  const { slug } = useParams();
  const c = findClinicBySlug(slug);
  if (!c) {
    // Unknown slug — send the visitor to the directory rather than a bare 404
    // so the crawl-path stays cohesive. The routing helper below only mounts
    // this component for slugs that ARE in the clinic set, so this branch
    // exists purely as a defensive fallback.
    return <Navigate to="/locations" replace />;
  }

  return (
    <div data-testid={`clinic-page-${c.slug}`}>
      <SEO
        title={`${c.display_name} | Peptide Therapy Consultations`}
        description={`VidaVital Medical clinic at ${c.address_full}. In-clinic peptide-therapy consultations with licensed providers, plus telehealth for patients located in ${c.telehealth_state}, with a ${c.telehealth_state}-licensed provider, where clinically appropriate.`}
        path={`/locations/${c.slug}`}
        schema={buildSchema(c)}
        breadcrumbs={[
          { name: "Locations", path: "/locations" },
          { name: `${c.name}, ${c.state}`, path: `/locations/${c.slug}` },
        ]}
      />

      {/* ============================================================ HERO */}
      <section className="vv-hero">
        <div className="vv-container py-12 md:py-16 grid lg:grid-cols-[1.2fr_1fr] gap-10">
          <div>
            <Breadcrumbs
              items={[
                { name: "Locations", path: "/locations" },
                { name: `${c.name}, ${c.state}`, path: `/locations/${c.slug}` },
              ]}
            />
            <div className="vv-hero-eyebrow mb-2 mt-3">{c.state_full}</div>
            <h1
              data-testid={`clinic-hero-h1-${c.slug}`}
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
            >
              {c.display_name}
            </h1>
            <p
              data-testid={`clinic-hero-intro-${c.slug}`}
              className="mt-5 text-stone-600 md:text-lg leading-relaxed max-w-2xl"
            >
              {c.intro_line}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <ScheduleConsultationCTA location={`clinic-${c.slug}-hero`} variant="gold" size="lg">
                Request Consultation
              </ScheduleConsultationCTA>
              <a
                href={`tel:${c.phone_e164}`}
                onClick={() => trackPhoneCall(`clinic_${c.slug}_hero`)}
                data-testid={`clinic-phone-${c.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-5 py-3 text-sm font-semibold text-[color:var(--vv-ink)] hover:border-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal)]"
              >
                <Phone size={15} /> {c.phone_display}
              </a>
            </div>
          </div>

          {/* NAP CARD */}
          <aside className="rounded-2xl border border-stone-200 bg-white p-6 md:p-7 shadow-sm">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--vv-gold-deep)] font-semibold">Clinic</div>
            <div data-testid={`clinic-nap-${c.slug}`} className="mt-3 space-y-2 text-sm text-stone-800 leading-relaxed">
              <div className="font-semibold text-stone-900">{c.display_name}</div>
              <div className="text-stone-700">{c.address_line_1}<br />{c.address_line_2}<br />{c.city}, {c.state} {c.zip}</div>
              <a
                href={`tel:${c.phone_e164}`}
                onClick={() => trackPhoneCall(`clinic_${c.slug}_nap_card`)}
                className="inline-flex items-center gap-1.5 text-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal-hover)] font-semibold"
                data-testid={`clinic-nap-phone-${c.slug}`}
              >
                <Phone size={14} /> {c.phone_display}
              </a>
              <div className="flex items-center gap-1.5 text-stone-700">
                <Mail size={14} /> <a href={`mailto:${c.email}`} className="hover:text-[color:var(--vv-teal)]">{c.email}</a>
              </div>
              <div className="flex items-start gap-1.5 text-stone-700"><Clock size={14} className="mt-0.5" /> <span>{c.hours}</span></div>
              <div className="flex items-start gap-1.5 text-stone-700"><Video size={14} className="mt-0.5" /> <span>{c.modality}</span></div>
              <div className="pt-3 mt-3 border-t border-stone-100 text-xs text-stone-500 leading-relaxed">
                <div data-testid={`clinic-parking-${c.slug}`}><strong className="text-stone-700">Parking / directions.</strong> {c.parking_note}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ================================================ DIRECT ANSWER */}
      <section className="vv-container py-10 md:py-14 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-display text-2xl md:text-3xl">What a provider-led peptide consultation includes</h2>
          <p className="text-stone-700 leading-relaxed">
            At the {c.name} clinic, your first visit is with a licensed VidaVital Medical provider. The provider reviews your goals, medical history, current medications, and any relevant labs, then discusses whether peptide therapy may be appropriate for your situation. If it is not, the provider says so and outlines alternatives.
          </p>
          <p className="text-stone-700 leading-relaxed">
            When peptide therapy is clinically appropriate, your provider outlines a personalized plan, coordinates any required pharmacy compounding, and sets a follow-up cadence to monitor response and safety. All prescribing decisions follow applicable federal and {c.state_full} state laws.
          </p>
          {c.iv_therapy_available && (
            <p
              data-testid={`clinic-iv-therapy-${c.slug}`}
              className="text-stone-700 leading-relaxed"
            >
              IV therapy is available on-site at the {c.name} clinic and is administered by licensed registered nurses (RNs) only. Whether IV therapy is appropriate for you is determined during your provider-led consultation, and it is offered alongside &mdash; not in place of &mdash; a full medical evaluation.
            </p>
          )}
        </div>
        <aside className="rounded-xl border border-stone-200 bg-stone-50 p-5 text-sm text-stone-700 leading-relaxed" data-testid={`clinic-consult-checklist-${c.slug}`}>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Every consultation covers</div>
          <ul className="mt-3 space-y-2">
            <li className="flex gap-2"><Stethoscope size={14} className="mt-0.5 text-[color:var(--vv-teal)]" /> Goals + relevant medical history review</li>
            <li className="flex gap-2"><TestTube2 size={14} className="mt-0.5 text-[color:var(--vv-teal)]" /> Whether labs are indicated before treatment</li>
            <li className="flex gap-2"><ClipboardList size={14} className="mt-0.5 text-[color:var(--vv-teal)]" /> Candid eligibility assessment and alternatives</li>
            <li className="flex gap-2"><ShieldCheck size={14} className="mt-0.5 text-[color:var(--vv-teal)]" /> Risks, monitoring, and regulatory-status transparency</li>
            <li className="flex gap-2"><Video size={14} className="mt-0.5 text-[color:var(--vv-teal)]" /> Telehealth follow-up for patients located in {c.state_full}, with a {c.state_full}-licensed provider</li>
          </ul>
        </aside>
      </section>

      {/* ============================================= TREATMENT CATEGORIES */}
      <section className="bg-stone-50 border-y border-stone-200">
        <div className="vv-container py-12 md:py-16">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold-deep)] font-semibold">Locally available at {c.name}</div>
          <h2 className="mt-3 font-display text-2xl md:text-3xl">Peptide-therapy categories your provider may discuss</h2>
          <p className="mt-3 text-stone-600 max-w-2xl text-sm">Categories below link to the site's canonical treatment references. Whether any category is appropriate for you depends on your medical history and provider evaluation. Some peptides are investigational or compounded; regulatory status is discussed on each canonical page.</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TREATMENT_CATEGORIES.map((t) => (
              <Link
                key={t.title}
                to={t.href}
                data-testid={`clinic-treatment-${c.slug}-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group rounded-xl border border-stone-200 bg-white p-5 hover:border-[color:var(--vv-teal)] hover:shadow-md transition"
              >
                <div className="font-semibold text-stone-900 group-hover:text-[color:var(--vv-teal)]">{t.title}</div>
                <div className="mt-1.5 text-sm text-stone-600 leading-relaxed">{t.blurb}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--vv-teal)]">Read the canonical page <ArrowRight size={12} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================= ELIGIBILITY / REG */}
      <section className="vv-container py-12 md:py-16 grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="font-display text-2xl md:text-3xl">Eligibility, monitoring, and telehealth</h2>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Not every visitor is a candidate for peptide therapy. Your provider considers your medical history, current medications, existing conditions, and the goals you describe when deciding whether treatment is safe and reasonable to try. When a therapy is prescribed, your provider explains the expected monitoring cadence, potential adverse effects, and what to watch for.
          </p>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Follow-up visits can be in-clinic in {c.name}, {c.state} or by secure video for patients located in {c.state_full}, with a {c.state_full}-licensed provider, where clinically appropriate. Interstate telehealth is limited by state licensure and applicable telemedicine laws.
          </p>
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl">Pharmacy coordination and regulatory status</h2>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Some peptides prescribed in clinical settings are FDA-approved drugs (for example, GLP-1 agonists for the indications listed in their approved labeling). Others are compounded by state-licensed pharmacies for individual patients under a valid prescription. Compounded preparations are not individually FDA-approved and are used off-label under provider supervision.
          </p>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Your provider explains the regulatory status of each option, including risks and alternatives, before you decide whether to proceed. Learn more via authoritative resources at{" "}
            <a href="https://www.fda.gov/drugs/human-drug-compounding/compounding-and-fda-questions-and-answers" target="_blank" rel="noopener noreferrer" className="underline hover:text-[color:var(--vv-teal)]">FDA on drug compounding</a>{" "}and{" "}
            <a href="https://www.nih.gov/health-information" target="_blank" rel="noopener noreferrer" className="underline hover:text-[color:var(--vv-teal)]">NIH health information</a>.
          </p>
        </div>
      </section>

      {/* ============================================= SERVICE AREA */}
      <section className="bg-stone-50 border-t border-stone-200">
        <div className="vv-container py-10 md:py-14">
          <h2 className="font-display text-2xl md:text-3xl">Service area &amp; nearby communities</h2>
          <p className="mt-3 text-stone-600 text-sm max-w-2xl">
            In-clinic visits welcome patients from the {c.metro} area, including communities such as those listed below. Telehealth eligibility for patients located in {c.state_full} (with a {c.state_full}-licensed provider) is confirmed during intake and depends on applicable telemedicine laws.
          </p>
          <ul
            data-testid={`clinic-service-area-${c.slug}`}
            className="mt-5 flex flex-wrap gap-2"
          >
            {c.nearby_communities.map((comm) => (
              <li
                key={comm}
                data-testid={`clinic-service-area-item-${c.slug}-${comm.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700"
              >
                <MapPin size={11} className="text-[color:var(--vv-teal)]" /> {comm}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-stone-500 leading-relaxed max-w-3xl">
            Communities listed above are provided for factual navigation context. They are not separate service areas or standalone offerings. All medical care is delivered from the {c.name} clinic address and through telehealth with a {c.state_full}-licensed provider, for patients located in {c.state_full}, where clinically appropriate.
          </p>
        </div>
      </section>

      {/* ============================================= LOCAL FAQ */}
      <section className="vv-container py-12 md:py-16 max-w-3xl">
        <h2 className="font-display text-2xl md:text-3xl">{c.name} clinic FAQ</h2>
        <div className="mt-6 divide-y divide-stone-200 border-y border-stone-200">
          {c.local_faq.map((f, i) => (
            <details
              key={f.q}
              data-testid={`clinic-faq-${c.slug}-${i}`}
              className="group py-4"
              open={i === 0}
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                <span className="font-semibold text-stone-900">{f.q}</span>
                <span className="text-[color:var(--vv-gold-deep)] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-stone-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ============================================= RELATED / CTA */}
      <section className="bg-[color:var(--vv-ink)] text-white">
        <div className="vv-container py-12 md:py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight">Ready to talk with a licensed provider?</h2>
            <p className="mt-3 text-stone-300 leading-relaxed">Submit the consultation request and a member of our care team will follow up to schedule your visit at the {c.name} clinic or by telehealth with a {c.state_full}-licensed provider, for patients located in {c.state_full}, if clinically appropriate.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <ScheduleConsultationCTA location={`clinic-${c.slug}-footer`} variant="gold" size="lg">Request Consultation</ScheduleConsultationCTA>
              <a href={`tel:${c.phone_e164}`} onClick={() => trackPhoneCall(`clinic_${c.slug}_footer`)} className="inline-flex items-center gap-2 rounded-full border border-stone-600 px-5 py-3 text-sm font-semibold text-white hover:border-[color:var(--vv-gold)]"><Phone size={15} /> {c.phone_display}</a>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold)] font-semibold">Related</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {RELATED_LINKS.map((l) => {
                const Icon = l.icon;
                return (
                  <Link
                    key={l.href}
                    to={l.href}
                    data-testid={`clinic-related-${c.slug}-${l.href.replace(/[^a-z0-9]+/gi, "-")}`}
                    className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-stone-100 hover:border-[color:var(--vv-gold)] hover:text-white"
                  >
                    <Icon size={14} className="text-[color:var(--vv-gold)]" /> {l.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= OTHER LOCATIONS */}
      <section className="vv-container py-10">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold">Other VidaVital locations</div>
        <ul className="mt-3 flex flex-wrap gap-2">
          {CLINICS.filter((x) => x.slug !== c.slug).map((x) => (
            <li key={x.slug}>
              <Link
                to={`/locations/${x.slug}`}
                data-testid={`clinic-crosslink-${c.slug}-${x.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs text-stone-700 hover:border-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal)]"
              >
                <MapPin size={11} /> {x.name}, {x.state}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/locations" data-testid={`clinic-crosslink-${c.slug}-directory`} className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--vv-ink)] text-white px-3 py-1.5 text-xs hover:opacity-95">
              All locations <ArrowRight size={11} />
            </Link>
          </li>
        </ul>
      </section>

      {/* ============================================= FOOTER DISCLAIMER */}
      <section className="vv-container pb-14 text-xs text-stone-500 leading-relaxed max-w-3xl">
        <p>
          <strong>Please note:</strong> Treatment recommendations are individualized. Every patient is evaluated by a licensed medical provider. Eligibility depends on medical history, provider assessment, and applicable state and federal laws, including {c.state_full} telemedicine regulations. Peptide therapies are provided only when clinically appropriate. Information on this page is educational and does not replace personalized medical advice, diagnosis, or treatment. This page does not create a provider-patient relationship. Read our{" "}
          <Link to="/medical-disclaimer" className="underline hover:text-stone-700">medical disclaimer</Link>.
        </p>
      </section>
    </div>
  );
}
